import { hatcheryDropdownModes } from "@/app/theme";
import { useSearchParams } from "react-router";
import { SingleSelectDropdown } from "@carbon6/c6-frontend-design";
import { FilterBar } from "@/components/filter";
import { StorageCleanupPanel } from "./StorageCleanupPanel";
import { InvalidEpisodesPanel } from "./InvalidEpisodesPanel";
import type { ReactNode } from "react";
import { GnbMenu } from "@/components/base";
import { PageBase, type PageBaseProps } from "@/page/base/PageBase";
import { UploadRecoveryPanel } from "./UploadRecoveryPanel";

// 얇은 셸. 로직은 각 패널(함수형)이 소유한다(멤버 관리 페이지와 같은 구조).
export function AdminStoragePage() {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab");
  const tab = requestedTab === "cleanup" || requestedTab === "invalid" ? requestedTab : "stale";
  const change = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("page");
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  const status =
    params.get("status") === "archived" || params.get("status") === "cancelled"
      ? params.get("status")!
      : "all";
  // 기본은 invalid 목록이다. 수용 목록은 명시적으로 고른다.
  const invalidState = params.get("state") === "accepted" ? "accepted" : "invalid";
  const filterBar =
    tab === "stale" ? undefined : (
      <FilterBar
        ariaLabel="Storage 운영 필터"
        chips={
          tab === "cleanup"
            ? status === "all"
              ? []
              : [
                  {
                    key: "status",
                    label: status,
                    removeLabel: "상태 해제",
                    onRemove: () => change("status", null),
                  },
                ]
            : invalidState === "accepted"
              ? [
                  {
                    key: "state",
                    label: "수용됨",
                    removeLabel: "상태 해제",
                    onRemove: () => change("state", null),
                  },
                ]
              : []
        }
        onClearAll={() => change(tab === "cleanup" ? "status" : "state", null)}
      >
        {tab === "cleanup" ? (
          <SingleSelectDropdown
            label="상태"
            mode={hatcheryDropdownModes.filter}
            neutralValue="all"
            value={status}
            options={[
              { value: "all", label: "전체" },
              { value: "cancelled", label: "cancelled" },
              { value: "archived", label: "archived" },
            ]}
            onValueChange={(value) => change("status", value === "all" ? null : value)}
          />
        ) : (
          <SingleSelectDropdown
            label="상태"
            mode={hatcheryDropdownModes.filter}
            neutralValue="invalid"
            value={invalidState}
            options={[
              { value: "invalid", label: "Invalid" },
              { value: "accepted", label: "수용됨" },
            ]}
            onValueChange={(value) => change("state", value === "invalid" ? null : value)}
          />
        )}
      </FilterBar>
    );

  return (
    <AdminStoragePageView
      pageTitle="Storage"
      currentMenu={GnbMenu.AdminStorage}
      requiredRoles={["admin"]}
      belowTabs={filterBar}
      bodyFlushTop={tab !== "stale"}
      tabs={{
        items: [
          { value: "stale", label: "멈춘/취소된 업로드" },
          { value: "cleanup", label: "스토리지 회수" },
          { value: "invalid", label: "Invalid 관리" },
        ],
        value: tab,
        onValueChange: (value) => setParams({ tab: value }),
        ariaLabel: "Storage 운영 탭",
      }}
    />
  );
}

class AdminStoragePageView extends PageBase<PageBaseProps> {
  // 활성 탭만 마운트한다. 그래서 비활성 탭의 운영 목록은 조회를 멈춘다.
  protected override renderContent(): ReactNode {
    if (this.props.tabs?.value === "cleanup") return <StorageCleanupPanel />;
    if (this.props.tabs?.value === "invalid") return <InvalidEpisodesPanel />;
    return <UploadRecoveryPanel />;
  }
}
